import { useState, useEffect } from 'react';
import type { DrinkTemplate, DrinkCategory } from '../../types';

interface DrinkCatalogProps {
  onAddDrink: (templateId: string) => void;
  onClose: () => void;
}

const categoryLabels: Record<DrinkCategory, string> = {
  beer: 'Pivo',
  wine: 'Víno',
  spirit: 'Destiláty',
  other: 'Ostatní'
};

export default function DrinkCatalog({ onAddDrink, onClose }: DrinkCatalogProps) {
  const [drinks, setDrinks] = useState<DrinkTemplate[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<DrinkCategory | 'all'>('all');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchDrinks = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || '';
        const response = await fetch(`${apiUrl}/api/drinks`);
        if (response.ok) {
          const data = await response.json();
          setDrinks(data.drinks || []);
        }
      } catch {
        // Handle error silently
      } finally {
        setIsLoading(false);
      }
    };

    fetchDrinks();
  }, []);

  const filteredDrinks = selectedCategory === 'all'
    ? drinks
    : drinks.filter(d => d.category === selectedCategory);

  const categories: (DrinkCategory | 'all')[] = ['all', 'beer', 'wine', 'spirit', 'other'];

  const handleDrinkSelect = (templateId: string) => {
    onAddDrink(templateId);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-end">
      <div className="w-full bg-gray-800 rounded-t-2xl max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold">Katalog nápojů</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white"
          >
            ✕
          </button>
        </div>

        {/* Category tabs */}
        <div className="flex gap-2 p-4 overflow-x-auto">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                selectedCategory === cat
                  ? 'bg-beer text-gray-900 font-medium'
                  : 'bg-gray-700 text-gray-300'
              }`}
            >
              {cat === 'all' ? 'Vše' : categoryLabels[cat]}
            </button>
          ))}
        </div>

        {/* Drink list */}
        <div className="flex-1 overflow-y-auto p-4">
          {isLoading ? (
            <div className="text-center text-gray-400 py-8">Načítám...</div>
          ) : filteredDrinks.length === 0 ? (
            <div className="text-center text-gray-400 py-8">Žádné nápoje</div>
          ) : (
            <div className="space-y-2">
              {filteredDrinks.map((drink) => (
                <button
                  key={drink.id}
                  onClick={() => handleDrinkSelect(drink.id)}
                  className="w-full flex items-center justify-between p-4 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors"
                >
                  <div className="text-left">
                    <div className="font-medium">{drink.name}</div>
                    <div className="text-sm text-gray-400">
                      {drink.volumeMl} ml • {drink.alcoholPercent}%
                    </div>
                  </div>
                  <span className="text-2xl">+</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
